import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { apprenticeApi } from '../api/apprentices';
import axios from 'axios';

interface OTJRecord {
  id: string;
  activity_date: string;
  hours: number;
  activity_type: string;
  description: string;
  evidence_url?: string;
  verification_status: 'pending' | 'approved' | 'rejected';
  verified_by?: string;
  verified_at?: string;
  created_at: string;
}

interface OTJSummary {
  total_hours_logged: number;
  total_hours_verified: number;
  required_hours: number;
  progress_percentage: number;
}

export const MyOTJHours: React.FC = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState<OTJRecord[]>([]);
  const [summary, setSummary] = useState<OTJSummary | null>(null);
  const [expandedRecord, setExpandedRecord] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOTJData = useCallback(async () => {
    if (!user?.tenant_id || !user?.user_id) return;

    try {
      setLoading(true);

      const apprenticesResponse = await apprenticeApi.getAll(user.tenant_id);
      const myApprentice = apprenticesResponse.data.find(
        (a: any) => a.canvas_user_id === user.user_id || a.email === user.email
      );

      if (!myApprentice) {
        console.error('No apprentice record found');
        return;
      }

      const recordsResponse = await axios.get(
        `/api/tracking/otj/tenant/${user.tenant_id}/apprentice/${myApprentice.id}`
      );
      setRecords(recordsResponse.data);

      const summaryResponse = await axios.get(
        `/api/tracking/otj/tenant/${user.tenant_id}/apprentice/${myApprentice.id}/summary`
      );
      setSummary(summaryResponse.data);
    } catch (error) {
      console.error('Failed to load OTJ data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, user?.user_id, user?.email]);

  useEffect(() => {
    loadOTJData();
  }, [loadOTJData]);

  const toggleExpand = (recordId: string) => {
    setExpandedRecord(expandedRecord === recordId ? null : recordId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getActivityTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      training: 'Training Session',
      mentoring: 'Mentoring',
      'self-study': 'Self-Study',
      workshop: 'Workshop',
      shadowing: 'Job Shadowing',
      research: 'Research & Development',
      other: 'Other'
    };
    return types[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      approved: { label: '✓ Verified', color: 'bg-green-100 text-green-800' },
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' }
    };
    return badges[status as keyof typeof badges] || badges.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading your OTJ hours...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My OTJ Hours</h1>
        <p className="text-gray-600">View your off-the-job training activity log</p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Hours Logged</p>
            <p className="text-3xl font-bold text-blue-600">{summary.total_hours_logged}</p>
            <p className="text-xs text-gray-500 mt-1">Total recorded hours</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Hours Verified</p>
            <p className="text-3xl font-bold text-green-600">{summary.total_hours_verified}</p>
            <p className="text-xs text-gray-500 mt-1">
              of {summary.required_hours} required
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Progress</p>
            <p className="text-3xl font-bold text-purple-600">{summary.progress_percentage}%</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(summary.progress_percentage, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <Link
          to="/tracking/otj/new"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Log New OTJ Hours
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Activity Log</h2>
          <p className="text-sm text-gray-600 mt-1">{records.length} entries</p>
        </div>

        {records.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-4">You haven't logged any OTJ hours yet.</p>
            <Link
              to="/tracking/otj/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Log Your First Entry
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {records.map((record) => {
              const isExpanded = expandedRecord === record.id;
              const statusBadge = getStatusBadge(record.verification_status);
              
              return (
                <div key={record.id} className="hover:bg-gray-50 transition">
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => toggleExpand(record.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {formatDate(record.activity_date)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getActivityTypeLabel(record.activity_type)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                          {record.hours} {record.hours === 1 ? 'hour' : 'hours'}
                        </p>
                        {!isExpanded && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                            {record.description}
                          </p>
                        )}
                      </div>
                      <div className="ml-4">
                        <svg
                          className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>
                          <p className="text-gray-700">{record.description}</p>
                        </div>

                        {record.evidence_url && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Evidence</h4>
                            
                              href={record.evidence_url}
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Evidence
                            </a>
                          </div>
                        )}

                        {record.verification_status === 'approved' && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-green-900 mb-1">
                              Verified
                            </h4>
                            <p className="text-sm text-green-800">
                              Verified on {formatDate(record.verified_at!)}
                            </p>
                          </div>
                        )}

                        {record.verification_status === 'rejected' && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-red-900 mb-1">
                              Rejected
                            </h4>
                            <p className="text-sm text-red-800">
                              This entry was not approved
                            </p>
                          </div>
                        )}

                        <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                          Logged on {formatDate(record.created_at)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
